import { useState, SyntheticEvent, ReactNode } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import ImprovementTips from '../components/efficiency/ImprovementTips';
import SectorBenchmarks from '../components/efficiency/SectorBenchmarks';
import EnergyDistribution from '../components/efficiency/EnergyDistribution';
import AnomaliesAlerts from '../components/efficiency/AnomaliesAlerts';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

// TabPanel component for displaying tab content
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`efficiency-metrics-tabpanel-${index}`}
      aria-labelledby={`efficiency-metrics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `efficiency-metrics-tab-${index}`,
    'aria-controls': `efficiency-metrics-tabpanel-${index}`,
  };
};

const EfficiencyMetrics = () => {
  const [loading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Energy Efficiency Metrics (Beta)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Detailed analysis of your building's energy efficiency performance and optimization opportunities.
        </Typography>
      </Box>
      
      {/* Subtabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="efficiency metrics tabs"
            variant="fullWidth"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ 
              px: 2, 
              pt: 2,
              '& .MuiTab-root': {
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: 48,
                '&.Mui-selected': {
                  fontWeight: 700,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '8px 8px 0 0',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '25%',
                    width: '50%',
                    height: 3,
                    backgroundColor: 'primary.main',
                    borderRadius: '3px 3px 0 0'
                  }
                }
              }
            }}
          >
            <Tab label="Improvement Tips" {...a11yProps(0)} />
            <Tab label="Sector Benchmarks" {...a11yProps(1)} />
            <Tab label="Energy Distribution" {...a11yProps(2)} />
            <Tab label="Anomalies & Alerts" {...a11yProps(3)} />
          </Tabs>
        </Box>
        
        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <ImprovementTips />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <SectorBenchmarks />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <EnergyDistribution />
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <AnomaliesAlerts />
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default EfficiencyMetrics; 